import React, { useRef, useEffect, useMemo, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { toggleVeto, finalizeScoring, setScoringCategory } from "./scattergoriesRoom"
import { LISTS } from "../../../data/scattergoriesLists"
import { computeScores } from "../../../utils/scattergoriesLogic"
import { Screen, SgButton, Pill, PlayerDot, C, F } from "./ScattergoriesUI"
import { playSound } from "../../../utils/ballparkSounds"

const Stamp = ({ text, color }) => (
  <motion.div
    initial={{ scale: 2.5, opacity: 0, rotate: -35 }}
    animate={{ scale: 1, opacity: 0.12, rotate: -10 }}
    exit={{ scale: 1.5, opacity: 0 }}
    transition={{ type: "spring", stiffness: 450, damping: 14 }}
    style={{
      position: "absolute",
      right: 40,
      top: "15%",
      border: `3px dashed ${color}`,
      color: color,
      fontFamily: F.display,
      fontWeight: 900,
      fontSize: 14,
      letterSpacing: 2,
      padding: "4px 8px",
      borderRadius: 6,
      textTransform: "uppercase",
      transformOrigin: "center",
      pointerEvents: "none",
      zIndex: 0,
      background: `${color}08`,
      boxShadow: `0 0 0 2px ${color}15`,
    }}
  >
    {text}
  </motion.div>
)

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 15, scale: 0.98 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 350, damping: 25 },
  },
}

const ScoringScreen = ({ room, code, playerId, isHost, setShowScores }) => {
  const round = room.round || 1
  const listId = room.listId || 1
  const letter = room.letter || ""
  
  const listData = LISTS.find((l) => l.id === listId) || LISTS[0]
  const categories = listData.categories

  // Synchronized category index
  const activeCatIdx = room.activeScoringCat || 0

  const containerRef = useRef(null)

  // Scroll active category selector button into view
  useEffect(() => {
    if (containerRef.current) {
      const activeEl = containerRef.current.children[activeCatIdx]
      if (activeEl) {
        activeEl.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        })
      }
    }
  }, [activeCatIdx])

  const players = Object.entries(room.players)
  const roundAnswers = room.answers?.[round] || {}
  const roundVetoes = room.vetoes?.[round] || {}

  // Compute live scores, duplicates, and breakdowns reactively from Firestore state
  const { breakdown } = useMemo(
    () => computeScores(roundAnswers, roundVetoes, letter),
    [roundAnswers, roundVetoes, letter]
  )

  // Guards against a second tap landing before the first write round-trips and
  // silently toggling the veto back off.
  const [pendingVetoes, setPendingVetoes] = useState({})

  const handleVetoClick = async (targetPid, isCurrentlyVetoed) => {
    const key = `${activeCatIdx}:${targetPid}`
    if (pendingVetoes[key]) return
    setPendingVetoes((prev) => ({ ...prev, [key]: true }))

    if (!isCurrentlyVetoed) {
      playSound("lose")
    } else {
      playSound("correct")
    }
    try {
      await toggleVeto(code, round, activeCatIdx, targetPid, !isCurrentlyVetoed)
    } finally {
      setPendingVetoes((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
    }
  }

  const handleFinalize = async () => {
    playSound("correct")
    await finalizeScoring(code, round)
  }

  const activeCategoryName = categories[activeCatIdx]
  const activeBreakdown = breakdown[activeCatIdx] || {}

  return (
    <Screen style={{ gap: 14, maxHeight: "100dvh", overflowY: "auto" }}>
      {/* Top Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Pill>{code}</Pill>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.muted }}>
            Scoring · Round {round} of {room.totalRounds}
          </span>
        </div>
        <button
          onClick={() => {
            playSound("click")
            setShowScores(true)
          }}
          style={{
            background: C.panel,
            border: `1.5px solid ${C.borderDark}`,
            borderRadius: 12,
            padding: "4px 10px",
            fontFamily: F.display,
            fontWeight: 800,
            fontSize: 12,
            color: C.ink,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          🏆 Scores
        </button>
      </div>

      {/* Round Info Header Banner */}
      <div
        style={{
          background: C.card,
          border: `1.5px solid ${C.border}`,
          borderRadius: 20,
          padding: "12px 18px",
          boxShadow: `0 4px 0 ${C.border}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <div>
          <span style={{ fontSize: 11, fontWeight: 800, color: C.muted, letterSpacing: 0.5 }}>
            LETTER FOR THIS ROUND
          </span>
          <div style={{ fontFamily: F.display, fontWeight: 900, fontSize: 30, color: C.indigo, lineHeight: 1 }}>
            {letter}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <span style={{ fontSize: 11, fontWeight: 800, color: C.muted, letterSpacing: 0.5 }}>
            LIST PACK
          </span>
          <div style={{ fontSize: 14, fontWeight: 800, color: C.ink, fontFamily: F.display }}>
            {listData.name}
          </div>
        </div>
      </div>

      {/* Horizontal Category Selector Chips */}
      <div
        ref={containerRef}
        style={{
          display: "flex",
          gap: 6,
          overflowX: "auto",
          paddingBottom: 6,
          margin: "0 -4px",
          scrollBehavior: "smooth",
          flexShrink: 0,
        }}
      >
        {categories.map((_, idx) => (
          <button
            key={idx}
            disabled={!isHost}
            onClick={() => {
              if (isHost) {
                playSound("click")
                setScoringCategory(code, idx)
              }
            }}
            style={{
              padding: "6px 12px",
              borderRadius: 10,
              border: activeCatIdx === idx ? `1.5px solid ${C.indigo}` : `1.5px solid ${C.border}`,
              background: activeCatIdx === idx ? C.indigo : C.card,
              color: activeCatIdx === idx ? C.white : C.muted,
              fontWeight: 800,
              fontSize: 12,
              fontFamily: F.display,
              cursor: isHost ? "pointer" : "default",
              flexShrink: 0,
              opacity: activeCatIdx === idx ? 1 : 0.6,
              transition: "all 0.2s ease",
            }}
          >
            {idx + 1}
          </button>
        ))}
      </div>

      {/* Active Category Display */}
      <div
        style={{
          background: C.highlight,
          borderRadius: 18,
          padding: "16px",
          borderLeft: `5px solid ${C.indigo}`,
          boxShadow: "0 2px 6px rgba(79, 70, 229, 0.05)",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCatIdx}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            <span style={{ fontSize: 11, fontWeight: 800, color: C.indigo, letterSpacing: 0.5, fontFamily: F.display }}>
              CATEGORY {activeCatIdx + 1} OF 12
            </span>
            <div style={{ fontFamily: F.display, fontWeight: 900, fontSize: 20, color: C.ink, marginTop: 4 }}>
              {activeCategoryName}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Players Answers List for the active Category */}
      <motion.div
        key={activeCatIdx} // re-mounts/re-animates children on category change
        variants={containerVariants}
        initial="hidden"
        animate="show"
        style={{
          flex: 1,
          // Every sibling is flexShrink: 0, so this pane absorbs all overflow.
          // As a scroll container its min-height resolves to 0, which let the
          // host's taller footer squeeze it out of existence on small phones.
          minHeight: 160,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 10,
          paddingRight: 4,
        }}
      >
        {players.map(([pid, p]) => {
          const pb = activeBreakdown[pid] || { answer: "", isDuplicate: false, isVetoed: false, points: 0, startsWithLetter: false }
          const ans = pb.answer.trim()
          const isEmpty = ans === ""

          return (
            <motion.div
              key={pid}
              variants={itemVariants}
              whileHover={isEmpty ? {} : { scale: 1.015, y: -2, boxShadow: "0 4px 10px rgba(0,0,0,0.04)" }}
              whileTap={isEmpty ? {} : { scale: 0.995 }}
              onClick={() => !isEmpty && handleVetoClick(pid, pb.isVetoed)}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                background: pb.points > 0 ? C.card : C.panel,
                border: pb.points > 0 ? `1.5px solid ${C.border}` : `1.5px solid ${C.borderDark}`,
                borderRadius: 14,
                padding: "12px 14px",
                cursor: isEmpty ? "default" : "pointer",
                opacity: isEmpty ? 0.6 : 1,
                boxShadow: "0 2px 4px rgba(0,0,0,0.01)",
                transition: "background 0.2s ease, border-color 0.2s ease",
                position: "relative",
                overflow: "hidden",
                minHeight: 68,
                boxSizing: "border-box",
              }}
            >
              {/* Stamp overlays */}
              <AnimatePresence>
                {!isEmpty && pb.isVetoed && (
                  <Stamp text="VETOED" color={C.coral} />
                )}
                {!isEmpty && !pb.isVetoed && pb.isDuplicate && (
                  <Stamp text="DUPLICATE" color={C.amberDark} />
                )}
              </AnimatePresence>

              <div style={{ marginTop: 2 }}>
                <PlayerDot color={p.color} />
              </div>
              
              <div style={{ flex: 1, minWidth: 0, zIndex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.muted }}>
                  {p.name} {pid === playerId && "(You)"}
                </div>
                
                <div style={{ marginTop: 2 }}>
                  {isEmpty ? (
                    <span style={{ fontStyle: "italic", color: C.muted, fontSize: 15 }}>
                      No Answer
                    </span>
                  ) : (
                    <span
                      style={{
                        fontWeight: 800,
                        fontSize: 15,
                        color: pb.points > 0 ? C.ink : C.muted,
                        textDecoration: pb.points > 0 ? "none" : "line-through",
                        wordBreak: "break-word",
                        display: "block",
                        lineHeight: 1.35,
                      }}
                    >
                      {pb.answer}
                    </span>
                  )}
                </div>

                {/* Status Badges */}
                {!isEmpty && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                    {!pb.startsWithLetter && (
                      <span style={{ fontSize: 10, fontWeight: 800, color: C.amberDark, background: "#FFFBEB", border: `1px solid ${C.amber}`, padding: "2px 6px", borderRadius: 6 }}>
                        ⚠️ Wrong Letter
                      </span>
                    )}
                    {pb.isDuplicate && (
                      <span style={{ fontSize: 10, fontWeight: 800, color: C.coralDark, background: "#FEF2F2", border: `1px solid ${C.coral}`, padding: "2px 6px", borderRadius: 6 }}>
                        Duplicate
                      </span>
                    )}
                    {pb.isVetoed && (
                      <span style={{ fontSize: 10, fontWeight: 800, color: C.muted, background: "#F3F4F6", border: "1px solid #D1D5DB", padding: "2px 6px", borderRadius: 6 }}>
                        Vetoed
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Point Indicator and Veto Toggle Icon */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0, zIndex: 1, marginTop: 2 }}>
                <span
                  style={{
                    fontFamily: F.display,
                    fontWeight: 900,
                    fontSize: 16,
                    color: pb.points > 0 ? C.green : C.muted,
                  }}
                >
                  +{pb.points} pt
                </span>
                
                {!isEmpty && (
                  <div
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 6,
                      border: `1.5px solid ${pb.isVetoed ? C.borderDark : C.green}`,
                      background: pb.isVetoed ? "transparent" : `${C.green}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: pb.isVetoed ? C.borderDark : C.green,
                      fontWeight: 900,
                      fontSize: 14,
                    }}
                  >
                    {pb.isVetoed ? "✗" : "✓"}
                  </div>
                )}
              </div>
            </motion.div>
          )
        })}
      </motion.div>

      {/* Review Footer Navigation */}
      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 8, flexShrink: 0 }}>
        {isHost ? (
          <div style={{ display: "flex", gap: 10 }}>
            <SgButton
              variant="panel"
              disabled={activeCatIdx === 0}
              onClick={() => {
                playSound("click")
                setScoringCategory(code, activeCatIdx - 1)
              }}
              style={{ height: 44, flex: 1, fontSize: 14 }}
            >
              ← Prev Category
            </SgButton>
            <SgButton
              variant="panel"
              disabled={activeCatIdx === 11}
              onClick={() => {
                playSound("click")
                setScoringCategory(code, activeCatIdx + 1)
              }}
              style={{ height: 44, flex: 1, fontSize: 14 }}
            >
              Next Category →
            </SgButton>
          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              fontWeight: 800,
              color: C.indigo,
              fontSize: 13,
              fontFamily: F.display,
              padding: "10px",
              background: C.highlight,
              borderRadius: 12,
              border: `1.5px dashed ${C.borderDark}`,
            }}
          >
            👥 Synchronized with Host · Reviewing Category {activeCatIdx + 1} of 12
          </div>
        )}

        {isHost ? (
          <SgButton
            onClick={handleFinalize}
            variant={activeCatIdx === 11 ? "indigo" : "panel"}
            style={{
              border: activeCatIdx === 11 ? "none" : `1.5px solid ${C.borderDark}`,
              fontWeight: 900,
              boxShadow: activeCatIdx === 11 ? `0 0 10px ${C.indigo}40` : "none",
            }}
          >
            Finalize Round Scores
          </SgButton>
        ) : (
          <div style={{ textAlign: "center", fontWeight: 700, color: C.muted, fontSize: 13, fontFamily: F.display, padding: 8 }}>
            Waiting for host to finalize scores…
          </div>
        )}
      </div>
    </Screen>
  )
}

export default ScoringScreen
